import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Info } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiService from '../../../services/apiService';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';
import TimePicker from '../../../components/TimePicker/TimePicker';

type Props = {
  onClose: () => void;
  editData?: any | null;
  onSuccess?: () => void;
  onError?: (message: string) => void;
};

type Option = {
  id: number | string;
  name: string;
};

type SiteOption = {
  id: number | string;
  name: string;
};

type UnitStationOption = {
  id: number | string;
  name: string;
};

type SystemOption = {
  id: number | string;
  name: string;
};

type RevOption = {
  id: number | string;
  rev: string;
};

type HbsOption = {
  id: number | string;
  hbs: string;
};

const AddSpecificInstructionModal: React.FC<Props> = ({ onClose, editData, onSuccess, onError }) => {
  // Form states
  const [title, setTitle] = useState('');
  const [site, setSite] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [unitStation, setUnitStation] = useState('');
  const [system, setSystem] = useState('');
  const [rev, setRev] = useState('');
  const [hbs, setHbs] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');

  // Dropdown options state
  const [siteOptions, setSiteOptions] = useState<Option[]>([]);
  const [unitStationOptions, setUnitStationOptions] = useState<Option[]>([]);
  const [systemOptions, setSystemOptions] = useState<Option[]>([]);
  const [revOptions, setRevOptions] = useState<RevOption[]>([]);
  const [hbsOptions, setHbsOptions] = useState<HbsOption[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const startDateRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const siteRef = useRef<HTMLSelectElement>(null);
  const unitStationRef = useRef<HTMLSelectElement>(null);
  const systemRef = useRef<HTMLSelectElement>(null);
  const revRef = useRef<HTMLSelectElement>(null);
  const hbsRef = useRef<HTMLSelectElement>(null);
  const reasonRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Error state for each field
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!editData;

  // Fetch dropdown options on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoading(true);
      setFetchError('');

      try {
        const [sitesRes, unitStationsRes, systemsRes, revsRes, hbsRes] = await Promise.all([
          apiService.get('/sites'),
          apiService.get('/temporary-operating-instructions/dropdowns/unit-stations'),
          apiService.get('/temporary-operating-instructions/dropdowns/systems'),
          apiService.get('/temporary-operating-instructions/dropdowns/revs'),
          apiService.get('/temporary-operating-instructions/dropdowns/hbs'),
        ]);

        setSiteOptions(Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data?.data ?? []));

        setUnitStationOptions(unitStationsRes.data || []);
        setSystemOptions(systemsRes.data || []);
        setRevOptions(revsRes.data || []);
        setHbsOptions(hbsRes.data || []);
      } catch (err: any) {
        console.error('Error fetching dropdown data:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load dropdown data';
        onError?.(errorMessage);
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Populate form fields when editData is provided
  useEffect(() => {
    if (editData) {
      setTitle(editData.title || '');

      // Handle site_id - check both direct ID and nested object
      if (editData.site_id) {
        setSite(editData.site_id.toString());
      } else if (editData.site) {
        // Find site by name if only name is provided
        const foundSite = siteOptions.find((s) => s.name === editData.site);
        if (foundSite) setSite(foundSite.id.toString());
      }

      setStartDate(editData.start_date || '');

      // Format time from "HH:mm:ss" to "HH:mm" for time input
      setStartTime(editData.start_time ? editData.start_time.substring(0, 5) : '');
      setEndTime(editData.end_time ? editData.end_time.substring(0, 5) : '');

      setEndDate(editData.end_date || '');

      // Handle unit_station_id
      if (editData.unit_station_id) {
        setUnitStation(editData.unit_station_id.toString());
      } else if (editData.unit_station) {
        const found = unitStationOptions.find((u) => u.name === editData.unit_station);
        if (found) setUnitStation(found.id.toString());
      }

      // Handle system_id
      if (editData.system_id) {
        setSystem(editData.system_id.toString());
      } else if (editData.system) {
        const found = systemOptions.find((s) => s.name === editData.system);
        if (found) setSystem(found.id.toString());
      }

      // Handle rev_id
      if (editData.rev_id) {
        setRev(editData.rev_id.toString());
      } else if (editData.rev) {
        const found = revOptions.find((r) => r.rev === editData.rev);
        if (found) setRev(found.id.toString());
      }

      // Handle hbs_id
      if (editData.hbs_id) {
        setHbs(editData.hbs_id.toString());
      } else if (editData.hbs) {
        const found = hbsOptions.find((h) => h.hbs === editData.hbs);
        if (found) setHbs(found.id.toString());
      }

      setReason(editData.reason_for_specific_instruction || '');
      setDescription(editData.description || editData.specific_instruction || '');
    }
  }, [editData, siteOptions, unitStationOptions, systemOptions, revOptions, hbsOptions]);

  const validateDateTime = (startDateValue: string, startTimeValue: string, endDateValue: string, endTimeValue: string) => {
    if (!startDateValue || !startTimeValue || !endDateValue || !endTimeValue) {
      return;
    }

    const start = new Date(`${startDateValue}T${startTimeValue}`);
    const end = new Date(`${endDateValue}T${endTimeValue}`);

    setErrors((prev) => {
      const updated = { ...prev };

      delete updated.endDate;
      delete updated.endTime;

      if (end <= start) {
        updated.endDate = 'End Date and End Time must be later than Start Date and Start Time.';
        updated.endTime = 'End Date and End Time must be later than Start Date and Start Time.';
      }

      return updated;
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.trim().length > 100) {
      newErrors.title = 'Title cannot exceed 100 characters';
    }
    if (!site) newErrors.site = 'Site is required';
    if (!startDate) newErrors.startDate = 'Start Date is required';
    if (!startTime) newErrors.startTime = 'Start Time is required';
    if (!endDate) newErrors.endDate = 'End Date is required';
    if (!endTime) newErrors.endTime = 'End Time is required';
    if (!unitStation) newErrors.unitStation = 'Unit-Station is required';
    if (!system) newErrors.system = 'System is required';
    if (!rev) newErrors.rev = 'Rev is required';
    if (!hbs) newErrors.hbs = 'HBS is required';

    if (!reason || reason === '<p><br></p>') newErrors.reason = 'Reason is required';
    if (!description || description === '<p><br></p>') newErrors.description = 'Description is required';

    if (startDate && startTime && endDate && endTime) {
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${endDate}T${endTime}`);

      if (end <= start) {
        newErrors.endDate = 'End Date and End Time must be later than Start Date and Start Time.';
        newErrors.endTime = 'End Date and End Time must be later than Start Date and Start Time.';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      return false;
    }

    return true;
  };

  const focusFirstError = (errors: Record<string, string>) => {
    const fieldRefs: Record<string, any> = {
      title: titleRef,
      site: siteRef,
      startDate: startDateRef,
      startTime: startTimeRef,
      endDate: endDateRef,
      endTime: endTimeRef,
      unitStation: unitStationRef,
      system: systemRef,
      rev: revRef,
      hbs: hbsRef,
      reason: reasonRef,
      description: descriptionRef,
    };

    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      setTimeout(() => {
        fieldRefs[firstErrorField].current?.focus?.();
      }, 300);
    }
  };

  const formatTimeForAPI = (time: string) => {
    // Ensure time is in H:i format (e.g., "14:30" not "14:30:00")
    if (time && time.length > 5) {
      return time.substring(0, 5);
    }
    return time;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        title: title.trim(),
        description: description,
        site_id: parseInt(site),
        start_date: startDate,
        end_date: endDate,
        start_time: formatTimeForAPI(startTime),
        end_time: formatTimeForAPI(endTime),
        unit_station_id: parseInt(unitStation),
        system_id: parseInt(system),
        rev_id: parseInt(rev),
        hbs_id: parseInt(hbs),
        reason_for_specific_instruction: reason,
        specific_instruction: description,
      };

      if (isEditMode) {
        // Update existing instruction
        await apiService.put(`/specific-instructions/${editData.id}`, payload);
      } else {
        // Create new instruction
        await apiService.post('/specific-instructions', payload);
      }

      // Success - trigger callback and close modal
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Error saving specific instruction:', err);
      const errorMessage =
        err?.response?.data?.message || err?.message || `Failed to ${isEditMode ? 'update' : 'create'} specific instruction`;
      onError?.(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const MAX_LENGTH = 1000;

  const getTextLength = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.innerText.length;
  };

  const clampHtmlToMaxLength = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.innerText;
    return text.length <= MAX_LENGTH ? html : text.slice(0, MAX_LENGTH);
  };

  const handleReasonChange = (value: string) => {
    if (getTextLength(value) <= MAX_LENGTH) {
      setReason(value);
    } else {
      setReason(clampHtmlToMaxLength(value));
    }
  };

  const handleDescriptionChange = (value: string) => {
    if (getTextLength(value) <= MAX_LENGTH) {
      setDescription(value);
    } else {
      setDescription(clampHtmlToMaxLength(value));
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-[900px] max-h-[90vh] rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-semibold">{isEditMode ? 'Edit Specific Instruction' : 'Add Specific Instruction'}</h2>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-5">
          {fetchError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{fetchError}</div>}

          <div className="grid grid-cols-2 gap-4">
            {/* Title */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-1">
                Title <Info className="w-3 h-3 text-gray-400" />
                <span className="text-red-500">*</span>
              </label>
              <input
                ref={titleRef}
                type="text"
                placeholder="Enter Title"
                maxLength={100}
                className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm bg-gray-100 ${
                  errors.title ? 'border-red-500' : ''
                }`}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);

                  if (errors.title) {
                    setErrors((prev) => ({
                      ...prev,
                      title: '',
                    }));
                  }
                }}
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${100 - title.length <= 10 ? 'text-red-500' : 'text-gray-400'}`}>
                  {100 - title.length} characters remaining
                </span>
              </div>
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Site */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-1">
                Site <Info className="w-3 h-3 text-gray-400" />
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  ref={siteRef}
                  className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm appearance-none bg-gray-100
                  ${!site ? 'text-gray-400' : 'text-gray-700'}
                  ${errors.site ? 'border-red-500' : ''}`}
                  value={site}
                  onChange={(e) => setSite(e.target.value)}
                  disabled={loading}
                >
                  <option value="" disabled hidden>
                    {loading ? 'Loading...' : siteOptions.length === 0 ? 'No sites available' : 'Select Site'}
                  </option>
                  {siteOptions.map((option) => (
                    <option key={option.id} value={option.id} className="text-gray-700">
                      {option.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.site && <p className="text-red-500 text-xs mt-1">{errors.site}</p>}
            </div>

            {/* Start Date */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Start Date
                <span className="text-red-500">*</span>
              </label>
              <StandardDatePicker
                inputRef={startDateRef}
                value={startDate}
                min={today}
                error={Boolean(errors.startDate)}
                onChange={(value) => {
                  setStartDate(value);
                  validateDateTime(value, startTime, endDate, endTime);
                }}
              />
              {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
            </div>

            {/* Start Time */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Start Time
                <span className="text-red-500">*</span>
              </label>
              <TimePicker
                inputRef={startTimeRef}
                value={startTime}
                error={errors.startTime}
                onChange={(value) => {
                  setStartTime(value);
                  validateDateTime(startDate, value, endDate, endTime);
                }}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                End Date
                <span className="text-red-500">*</span>
              </label>
              <StandardDatePicker
                inputRef={endDateRef}
                value={endDate}
                min={startDate || today}
                error={Boolean(errors.endDate)}
                onChange={(value) => {
                  setEndDate(value);
                  validateDateTime(startDate, startTime, value, endTime);
                }}
              />
              {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
            </div>

            {/* End Time */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                End Time
                <span className="text-red-500">*</span>
              </label>
              <TimePicker
                inputRef={endTimeRef}
                value={endTime}
                error={errors.endTime}
                onChange={(value) => {
                  setEndTime(value);
                  validateDateTime(startDate, startTime, endDate, value);
                }}
              />
            </div>

            {/* Unit-Station */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Unit-Station
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  ref={unitStationRef}
                  className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm appearance-none bg-gray-100
                  ${!unitStation ? 'text-gray-400' : 'text-gray-700'}
                  ${errors.unitStation ? 'border-red-500' : ''}`}
                  value={unitStation}
                  onChange={(e) => setUnitStation(e.target.value)}
                  disabled={loading}
                >
                  <option value="" disabled hidden>
                    {loading ? 'Loading...' : unitStationOptions.length === 0 ? 'No unit-stations available' : 'Select Unit-Station'}
                  </option>
                  {unitStationOptions.map((option) => (
                    <option key={option.id} value={option.id} className="text-gray-700">
                      {option.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.unitStation && <p className="text-red-500 text-xs mt-1">{errors.unitStation}</p>}
            </div>

            {/* System */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                System
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  ref={systemRef}
                  className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm appearance-none bg-gray-100
                  ${!system ? 'text-gray-400' : 'text-gray-700'}
                  ${errors.system ? 'border-red-500' : ''}`}
                  value={system}
                  onChange={(e) => setSystem(e.target.value)}
                  disabled={loading}
                >
                  <option value="" disabled hidden>
                    {loading ? 'Loading...' : systemOptions.length === 0 ? 'No systems available' : 'Select System'}
                  </option>
                  {systemOptions.map((option) => (
                    <option key={option.id} value={option.id} className="text-gray-700">
                      {option.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.system && <p className="text-red-500 text-xs mt-1">{errors.system}</p>}
            </div>

            {/* Rev */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-1">
                Rev <Info className="w-3 h-3 text-gray-400" />
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  ref={revRef}
                  className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm appearance-none bg-gray-100
                  ${!rev ? 'text-gray-400' : 'text-gray-700'}
                  ${errors.rev ? 'border-red-500' : ''}`}
                  value={rev}
                  onChange={(e) => setRev(e.target.value)}
                  disabled={loading}
                >
                  <option value="" disabled hidden>
                    {loading ? 'Loading...' : revOptions.length === 0 ? 'No revs available' : 'Select Rev'}
                  </option>
                  {revOptions.map((option) => (
                    <option key={option.id} value={option.id} className="text-gray-700">
                      {option.rev}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.rev && <p className="text-red-500 text-xs mt-1">{errors.rev}</p>}
            </div>

            {/* HBS */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-1">
                HBS <Info className="w-3 h-3 text-gray-400" />
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  ref={hbsRef}
                  className={`w-full border rounded px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 text-sm appearance-none bg-gray-100
                  ${!hbs ? 'text-gray-400' : 'text-gray-700'}
                  ${errors.hbs ? 'border-red-500' : ''}`}
                  value={hbs}
                  onChange={(e) => setHbs(e.target.value)}
                  disabled={loading}
                >
                  <option value="" disabled hidden>
                    {loading ? 'Loading...' : hbsOptions.length === 0 ? 'No HBS available' : 'Select HBS'}
                  </option>
                  {hbsOptions.map((option) => (
                    <option key={option.id} value={option.id} className="text-gray-700">
                      {option.hbs}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.hbs && <p className="text-red-500 text-xs mt-1">{errors.hbs}</p>}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="flex items-center gap-1 text-sm font-medium mb-2">
              Reason for Specific Instruction <Info className="w-3 h-3 text-gray-400" />
              <span className="text-red-500">*</span>
            </label>

            <div ref={reasonRef} tabIndex={-1} className="bg-gray-100 border border-gray-300 rounded">
              <ReactQuill
                theme="snow"
                value={reason}
                onChange={handleReasonChange}
                onKeyDown={(e) => {
                  if (
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter'].includes(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    getTextLength(reason) >= MAX_LENGTH
                  ) {
                    e.preventDefault();
                  }
                }}
                placeholder="Add description here"
                className="bg-gray-100"
              />
            </div>

            <div className="flex justify-between items-center mt-1">
              {errors.reason ? <p className="text-red-500 text-xs">{errors.reason}</p> : <span />}
              <p className={`text-xs ${getTextLength(reason) >= MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                {getTextLength(reason)}/{MAX_LENGTH}
              </p>
            </div>

            {/* {errors.reason && <p className="text-red-500 text-xs mt-1">{errors.reason}</p>} */}
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Specific Instructions
              <span className="text-red-500">*</span>
            </label>

            <div ref={descriptionRef} tabIndex={-1} className="bg-gray-100 border border-gray-300 rounded">
              <ReactQuill
                theme="snow"
                value={description}
                onChange={handleDescriptionChange}
                onKeyDown={(e) => {
                  if (
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter'].includes(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    getTextLength(description) >= MAX_LENGTH
                  ) {
                    e.preventDefault();
                  }
                }}
                placeholder="Add description here"
                className="bg-gray-100"
              />
            </div>

            <div className="flex justify-between items-center mt-1">
              {errors.description ? <p className="text-red-500 text-xs">{errors.description}</p> : <span />}
              <p className={`text-xs ${getTextLength(description) >= MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                {getTextLength(description)}/{MAX_LENGTH}
              </p>
            </div>

            {/* {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>} */}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-red-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : isEditMode ? 'Update' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSpecificInstructionModal;
